import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ChangeEvent, Ref, ReactNode, SelectHTMLAttributes, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { Calendar } from './Calendar.tsx';
import { toEnglishDigits } from '../lib/format.ts';

export function Field({ label, hint, error, required, children }:
  { label: string; hint?: string; error?: string | null; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}{required && <span className="text-rose-600"> *</span>}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
      {error && <span role="alert" className="mt-1 block text-xs text-rose-600">{error}</span>}
    </label>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput(props, ref) {
    // كل حقول كلمة المرور في البرنامج تمرّ من هنا — فالكشف يُضاف مرّةً ويصل إلى إحدى عشرة شاشة
    if (props.type === 'password') return <PasswordInput inputRef={ref} {...props} />;
    if (props.type === 'time') return <TimeInput inputRef={ref} {...props} onChange={arabicDigitsSafe(props)} />;
    return <input ref={ref} {...props} onChange={arabicDigitsSafe(props)}
      className={`control ${props.className ?? ''}`} />;
  },
);

/**
 * **خانةُ الأرقام تقبل العربية وتحوّلها** (قرار المالك 22/09/2026): من يكتب ١٠٠ بلوحة
 * مفاتيحه العربية يقصد مئة، فلا يُقال له «مبلغ غير صحيح». والتحويل يقع **قبل** أن يصل
 * النصّ إلى الشاشة، فتُخزَّن القيمةُ لاتينيةً ويرى الكاتبُ ما سيُحفظ لا شيئًا آخر.
 *
 * وخانات النصّ لا تُمسّ: اسمٌ أو ملاحظةٌ فيها رقمٌ عربي تبقى كما كتبها صاحبها. والحدّ
 * الفاصل هو `inputMode` — فهي إعلانُ الحقل عن نفسه أنه للأرقام.
 */
function arabicDigitsSafe(props: InputHTMLAttributes<HTMLInputElement>) {
  const numeric = props.inputMode === 'numeric' || props.inputMode === 'decimal' || props.inputMode === 'tel' || props.type === 'number';
  if (!numeric || !props.onChange) return props.onChange;
  // وفاصلة الآلاف الإنجليزية في خانات المبالغ تُحذف كالعربية: «15,000» = خمسة عشر ألفًا (المراجعة الشاملة 02/10/2026)
  const money = props.inputMode === 'numeric' || props.inputMode === 'decimal' || props.type === 'number';
  return (e: ChangeEvent<HTMLInputElement>) => {
    const ar = toEnglishDigits(e.target.value);
    const fixed = money ? ar.replace(/,/g, '') : ar;
    if (fixed !== e.target.value) e.target.value = fixed;
    props.onChange?.(e);
  };
}

/**
 * **كلمة المرور تُرى بطلب صاحبها** (قرار المالك 22/09/2026: «وهو بيكتب الباصورد يقدر
 * يشوفه ويخفيه علشان يصلّح الغلط لو بيكتب غلط أو مش واخد باله»).
 *
 * والكشف **بتبديل نوع الحقل** لا بحقلٍ نصّيٍّ ثانٍ يوازيه: الحقل واحد، وقيمته واحدة،
 * وترتيبه في النموذج واحد — ومدير كلمات المرور في المتصفّح يظلّ يعرفه حقلَ كلمة مرور
 * فيملؤه ويحفظه كما يفعل دائمًا.
 *
 * **ومُطفأٌ عند كل فتح، ولا يُحفظ اختياره**: الشاشة قد تكون بين الناس، فالسترُ هو الأصل
 * والكشفُ لحظةٌ تُطلب. ولا يُكتب الحرف المكشوف في سجلٍّ ولا يُرسل إلى أحد — التبديل
 * كلّه في المتصفّح.
 */
function PasswordInput({ inputRef, className, ...rest }:
  InputHTMLAttributes<HTMLInputElement> & { inputRef?: Ref<HTMLInputElement> }) {
  const [shown, setShown] = useState(false);
  return (
    <span className="relative block">
      <input ref={inputRef} {...rest} type={shown ? 'text' : 'password'}
        className={`control pe-11 ${className ?? ''}`} />
      <button type="button" disabled={rest.disabled} onClick={() => setShown(v => !v)}
        aria-pressed={shown} aria-label={shown ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
        title={shown ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
        className="absolute inset-y-0 end-0 flex w-11 items-center justify-center rounded-lg text-ink-500
          transition-colors hover:text-ink-900 disabled:opacity-40">
        {shown ? <EyeOff /> : <Eye />}
      </button>
    </span>
  );
}

/**
 * **حقلُ الوقت بساعةٍ ظاهرة تُختار منها** (قرار المالك 23/09/2026: «اى ادخال للساعه فى البرنامج تظهر
 * علامة الساعه بحيث يختار منها بدل ما يكتب، مثلا ف المتابعة مش موجوده»). رمزُ الساعة الذي يرسمه
 * المتصفّح لا يظهر في كل مكان — تطبيقُ أندرويد وبعضُ المتصفّحات بلا رمز، فيبقى الحقل خاناتٍ تُكتب.
 * فالزرّ هنا رمزُنا نحن في كل جهاز، والضغطُ عليه **أو على الحقل نفسه** يفتح منتقي الوقت
 * (`showPicker`)؛ والكتابة باقيةٌ لمن يريدها. ورمزُ المتصفّح يُخفى (index.css) فلا يظهر رمزان.
 */
function TimeInput({ inputRef, className, onClick, ...rest }:
  InputHTMLAttributes<HTMLInputElement> & { inputRef?: Ref<HTMLInputElement> }) {
  const own = useRef<HTMLInputElement | null>(null);
  const setRef = (el: HTMLInputElement | null) => {
    own.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = el;
  };
  const open = () => {
    const el = own.current;
    if (!el || el.disabled || el.readOnly) return;
    el.focus();
    try { el.showPicker?.(); } catch { /* متصفّحٌ قديم أو بلا لمسة مستخدم: يبقى الحقل مركَّزًا للكتابة */ }
  };
  return (
    <span className="relative block">
      <input ref={setRef} {...rest} type="time"
        onClick={e => { onClick?.(e); open(); }}
        className={`control control-time pr-11 ${className ?? ''}`} />
      <button type="button" tabIndex={-1} disabled={rest.disabled} onClick={open}
        aria-label="اختيار الوقت" title="اختيار الوقت"
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-lg text-brand-600
          transition-colors hover:text-brand-800 disabled:opacity-40">
        <Clock />
      </button>
    </span>
  );
}

const Clock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" className="h-[1.2rem] w-[1.2rem]">
    <circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" />
  </svg>
);

const EYE = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  className: 'h-[1.15rem] w-[1.15rem]', 'aria-hidden': true,
};
const Eye = () => (
  <svg {...EYE}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3.2" /></svg>
);
const EyeOff = () => (
  <svg {...EYE}><path d="M4 4l16 16" />
    <path d="M9.6 6c.8-.3 1.6-.5 2.4-.5 6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.2 4" />
    <path d="M6.3 8.2A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1.3 0 2.4-.3 3.5-.7" />
    <path d="M9.9 9.9a3.2 3.2 0 0 0 4.3 4.3" /></svg>
);
/**
 * حقل تاريخ **يوم/شهر/سنة** — لا بصيغة المتصفّح.
 *
 * `<input type="date">` يعرض التاريخ بلغة المتصفّح لا بلغة الصفحة: على متصفّح إنجليزي
 * يظهر 10/01/2026 ويُقرأ «١٠ يناير» وهو في الحقيقة **الأول من أكتوبر**. ولا سبيل إلى
 * تغيير ذلك بـCSS ولا بـ`lang` — فالحقل المعروض هنا نصٌّ نكتبه نحن بالترتيب المعتمد في
 * كل شاشات النظام (`shortDate`): `31/10/2026`.
 *
 * والقيمة المتبادلة تبقى ISO كما هي في القاعدة وفي كل نداءات الـAPI: `onChange` تُعطي
 * `YYYY-MM-DD` أو `''` — التنسيق عرضٌ لا تحويل بيانات.
 *
 * وزرّ التقويم يفتح لوحةً **مرسومة في الصفحة** (Calendar) لا منتقي نظام التشغيل:
 * ذاك يختلف شكله ولغته ومقاسه بين ويندوز وأندرويد وكل متصفّح، فيبدو غريبًا داخل
 * شاشةٍ عربية بخطٍّ واحد. وهذه بخطّ البرنامج وألوانه ومقاسه.
 */
export function DateInput({ value, onChange, className = '', id, name, required, disabled, max, min }: {
  value: string;
  onChange: (iso: string) => void;
  className?: string; id?: string; name?: string; required?: boolean; disabled?: boolean; max?: string; min?: string;
}) {
  const [text, setText] = useState(() => isoToDmy(value));
  const [open, setOpen] = useState(false);
  // القيمة قد تتغيّر من الخارج (زرّ «الشهر الحالي» مثلًا) فيتبعها النصّ
  // — إلّا نصًّا غلطًا فُرِّغت قيمته عمدًا عند الخروج: يبقى أمام صاحبه ليصلحه (02/10/2026)
  useEffect(() => { setText(t => (value === '' && t.trim() !== '' && dmyToIso(t) === null ? t : isoToDmy(value))); }, [value]);

  /**
   * **والمدى يُحرَس في الخانة المكتوبة كما في التقويم**: التقويم يُعطِّل ما خرج عن
   * `min`/`max`، لكن من كتب التاريخ بيده يتجاوزه — فيُعلَّم الحقل خطأً هنا أيضًا.
   * (والخادم يبقى الحارس الفاصل على كل حال؛ هذا ليقول للكاتب قبل أن يضغط «حفظ».)
   */
  const typed = dmyToIso(text);
  const outOfRange = typed !== null
    && ((min !== undefined && min !== '' && typed < min) || (max !== undefined && max !== '' && typed > max));
  const invalid = (text.trim() !== '' && typed === null) || outOfRange;

  return (
    /**
     * **تقويم البرنامج لا تقويم النظام** (قرار المالك): كان الزرّ يفتح
     * `input[type=date]` فيظهر صندوق نظام التشغيل — شكله ولغته ومقاسه ليست منّا،
     * فيبدو غريبًا داخل شاشةٍ عربية بخطٍّ واحد. والآن لوحةٌ مرسومة في الصفحة نفسها
     * (`Calendar`) بنفس الخطّ والألوان.
     *
     * **والكتابة باقية**: من يعرف التاريخ يكتبه أسرع ممّا ينقر عليه، فالخانة نصّية
     * كما كانت والتقويم إضافةٌ لا بديل.
     */
    <span className={`date-field relative inline-flex items-stretch gap-1 ${className}`}>
      <input
        id={id} name={name} type="text" inputMode="numeric" dir="ltr" placeholder="يوم/شهر/سنة"
        className="control" required={required} disabled={disabled}
        aria-invalid={invalid || undefined}
        data-min={min || undefined} data-max={max || undefined}
        value={text}
        onChange={e => {
          const v = toEnglishDigits(e.target.value);
          setText(v);
          const iso = dmyToIso(v);
          if (iso !== null) onChange(iso);
          else if (e.target.value.trim() === '') onChange('');
        }}
        // تصحيح الشكل بعد الكتابة (1/10/26 ← 01/10/2026). **والنصّ الغلط لا يُمسح بصمت ولا يُبقي القديم مربوطًا** (المراجعة
        // الشاملة 02/10/2026): كان التاريخ القديم يبقى قيمةَ الحقل فيُحفظ وهو غير ما على الشاشة — الآن يُفرَّغ والنصّ يبقى أحمر
        onBlur={() => {
          const short = dmyToIso(text, true);
          if (dmyToIso(text) === null && short !== null) { onChange(short); setText(isoToDmy(short)); return; }
          if (text.trim() !== '' && short === null) { if (value !== '') onChange(''); return; }
          setText(isoToDmy(value));
        }}
      />
      <button type="button" disabled={disabled}
        aria-label="اختيار من التقويم" aria-expanded={open} aria-haspopup="dialog"
        onClick={() => setOpen(o => !o)}
        className={`inline-flex min-h-9 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors ${
          open ? 'border-brand-600 bg-brand-600 text-white'
            : 'border-slate-200 bg-white text-ink-500 hover:border-brand-300 hover:text-brand-700'}`}>
        {/* أيقونة تقويم بخطّ رموز النظام — لا رمز Emoji يختلف شكله بين الأجهزة */}
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" aria-hidden="true" focusable="false">
          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
          <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
        </svg>
      </button>
      {open && !disabled && (
        <Calendar value={value} min={min} max={max}
          onPick={v => { onChange(v); setText(isoToDmy(v)); }}
          onClose={() => setOpen(false)} />
      )}
    </span>
  );
}

/** ISO ← شكل العرض: 2026-10-01 ⇄ 01/10/2026. والفارغ يبقى فارغًا. */
function isoToDmy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * شكل العرض ← ISO. يقبل `1/10/2026` و`01/10/2026` و`1-10-2026`، ويرفض ما ليس تاريخًا
 * حقيقيًا (31/02/2026 مثلًا) — فلا يُرسَل إلى الخادم تاريخٌ لا وجود له.
 */
function dmyToIso(text: string, shortYear = false): string | null {
  // والسنة من رقمين تُفهم 20xx **عند الخروج من الخانة وحده** (المراجعة الشاملة 02/10/2026): «5/10/26» كانت تُمسح بصمت.
  // وأثناء الكتابة لا — وإلّا قُرئت «05/10/20» تاريخًا كاملًا قبل أن تكتمل «2026»
  const m = (shortYear ? /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4}|\d{2})$/ : /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/).exec(text.trim());
  if (!m) return null;
  const [d, mo, y] = [Number(m[1]), Number(m[2]), m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])];
  const iso = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const dt = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(dt.getTime())) return null;
  // التحقّق بالعودة: 31/02 يصير 03/03 في Date، فلا يمرّ
  return dt.toISOString().slice(0, 10) === iso ? iso : null;
}

/**
 * حقل **شهر/سنة** — بنفس لوحة `DateInput` لا بصندوق المتصفّح.
 *
 * كانت شاشات المرتبات والمصاريف والتسويات وتصدير الشهر تستعمل `input[type=month]`،
 * فيفتح منتقي المتصفّح بشهورٍ إنجليزية (September 2026) وخطٍّ ليس خطّ البرنامج، داخل
 * شاشةٍ عربية بالكامل — قرار المالك: «أي عنصر إدخال تاريخ خلّيه زي اللي في حجز جديد».
 *
 * والقيمة المتبادلة تبقى `YYYY-MM` كما يقبلها الخادم في كل تلك المسارات، والمكتوب
 * `شهر/سنة` (`09/2026`) — ومن يعرف شهره يكتبه أسرع من أن ينقر عليه.
 */
export function MonthInput({ value, onChange, className = '', id, name, required, disabled, max, min }: {
  value: string;
  onChange: (month: string) => void;
  className?: string; id?: string; name?: string; required?: boolean; disabled?: boolean; max?: string; min?: string;
}) {
  const [text, setText] = useState(() => ymToMy(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(ymToMy(value)); }, [value]);

  const typed = myToYm(text);
  const outOfRange = typed !== null
    && ((min !== undefined && min !== '' && typed < min) || (max !== undefined && max !== '' && typed > max));
  const invalid = (text.trim() !== '' && typed === null) || outOfRange;

  return (
    <span className={`date-field relative inline-flex items-stretch gap-1 ${className}`}>
      <input
        id={id} name={name} type="text" inputMode="numeric" dir="ltr" placeholder="شهر/سنة"
        className="control" required={required} disabled={disabled}
        aria-invalid={invalid || undefined}
        data-min={min || undefined} data-max={max || undefined}
        value={text}
        onChange={e => {
          const v = toEnglishDigits(e.target.value);
          setText(v);
          const ym = myToYm(v);
          if (ym !== null) onChange(ym);
          else if (e.target.value.trim() === '') onChange('');
        }}
        onBlur={() => setText(ymToMy(value))}
      />
      <button type="button" disabled={disabled}
        aria-label="اختيار الشهر من التقويم" aria-expanded={open} aria-haspopup="dialog"
        onClick={() => setOpen(o => !o)}
        className={`inline-flex min-h-9 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors ${
          open ? 'border-brand-600 bg-brand-600 text-white'
            : 'border-slate-200 bg-white text-ink-500 hover:border-brand-300 hover:text-brand-700'}`}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" aria-hidden="true" focusable="false">
          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
          <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
        </svg>
      </button>
      {open && !disabled && (
        <Calendar mode="month" value={value} min={min} max={max}
          onPick={v => { onChange(v); setText(ymToMy(v)); }}
          onClose={() => setOpen(false)} />
      )}
    </span>
  );
}

/** `2026-09` ⇄ `09/2026`. والفارغ يبقى فارغًا. */
function ymToMy(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym.trim());
  return m ? `${m[2]}/${m[1]}` : '';
}

/** `09/2026` و`9/2026` و`9-2026` ← `2026-09`. وما ليس شهرًا (13) يُرفض. */
function myToYm(text: string): string | null {
  const m = /^(\d{1,2})[/\-.](\d{4})$/.exec(text.trim());
  if (!m) return null;
  const mo = Number(m[1]);
  if (mo < 1 || mo > 12) return null;
  return `${m[2]}-${String(mo).padStart(2, '0')}`;
}

// يقبل ref: شاشتا الشات تعيدان المؤشّر إلى الخانة بعد الإرسال (01/10/2026)
export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function TextArea(props, ref) {
    return <textarea ref={ref} {...props} className={`control ${props.className ?? ''}`} />;
  },
);
export function Select({ options, placeholder, ...props }:
  SelectHTMLAttributes<HTMLSelectElement> & { options: readonly { code: string; ar: string }[]; placeholder?: string }) {
  return (
    <select {...props} className={`control ${props.className ?? ''}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.code} value={o.code}>{o.ar}</option>)}
    </select>
  );
}

export function Badge({ tone, children }: { tone?: string; children: ReactNode }) {
  return <span className={`chip ${tone ?? 'bg-slate-100 text-ink-700'}`}>{children}</span>;
}

/** رؤوس البطاقات أوضح (قرار المالك: «وضّح رؤوس المواضيع»): أكبر، بلون العلامة، وتحتها خطّ يفصلها عمّا بعدها. */
export function Card({ title, action, children, className = '' }: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card p-4 sm:p-5 ${className}`}>
      {(title || action) && (
        <header className="mb-3 flex items-center justify-between gap-3 border-b-2 border-brand-100 pb-2">
          {title && <h2 className="text-lg font-bold text-brand-800">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function Stat({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div className="card p-3 text-center sm:p-4">
      <div className={`text-2xl font-bold tabular-nums ${tone ?? 'text-ink-900'}`}>{value}</div>
      <div className="mt-1 text-xs text-ink-500 sm:text-sm">{label}</div>
    </div>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'info' | 'success'; children: ReactNode }) {
  const tone = kind === 'error' ? 'bg-rose-50 text-rose-800 border-rose-200'
    : kind === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-brand-50 text-brand-700 border-brand-200';
  return <div role={kind === 'error' ? 'alert' : 'status'} className={`rounded-lg border px-3 py-2 text-sm ${tone}`}>{children}</div>;
}

export function Spinner({ label = 'جارٍ التحميل…' }: { label?: string }) {
  return <p role="status" className="py-6 text-center text-sm text-ink-500">{label}</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-ink-500">{children}</p>;
}

/**
 * درج جانبي حقيقي — خارج تدفّق المستند تمامًا.
 *
 * `side` **فيزيائي لا منطقي**: `left` تعني الحافة اليسرى من الشاشة مهما كان اتجاه الصفحة،
 * و`right` اليمنى. لذلك `left-0`/`right-0` لا `start`/`end` — فالواجهة RTL وكان الطرف
 * المنطقي سيعكسهما. مؤشرات الأداء تفتح يسارًا والإشعارات يمينًا، وهذا قرار عرض لا اتجاه لغة.
 *
 * **السبب الجذري** الذي كان يقصّ الدرج إلى ارتفاع الشريط العلوي: الشريط يحمل `backdrop-blur`،
 * وأي قيمة لـ`backdrop-filter` غير `none` **تُنشئ containing block لكل سليل `position: fixed`**
 * (وكذلك `transform` و`filter` و`perspective` و`contain`). فالدرج المرسوم داخل الشريط كان
 * يُثبَّت على صندوق الشريط لا على النافذة — قياسًا: 55px بدل 812px. ولذلك يُرسَل هنا عبر
 * **Portal إلى `document.body`**: يخرج من شجرة الشريط فيعود `fixed` إلى النافذة كما يجب.
 * هذا حلٌّ من الجذر لا التفاف — لا ارتفاع ثابت ولا هوامش ولا z-index يداري المشكلة.
 *
 * لماذا تبقى الصفحة ثابتة تحته:
 *   • الغلاف `fixed` بارتفاع النافذة: لا يشغل مساحة في التخطيط، فلا يوسّع الصفحة ولا يزيحها.
 *   • قفل تمرير الجسم أثناء الفتح، **مع تعويض عرض شريط التمرير** بحشوة مساوية له — لأن
 *     إخفاء الشريط وحده يوسّع منطقة العرض فتقفز الصفحة أفقيًا. هذه هي «حركة الصفحة» التي
 *     تُلاحَظ عادةً عند فتح الأدراج، ومصدرها الشريط لا الدرج.
 *   • التمرير كله داخل الدرج (`overflow-y-auto`) ولا يتسرّب إلى الصفحة.
 */
/**
 * **Esc يقفل الطبقة العليا وحدها** (المراجعة الشاملة 02/10/2026، وقرار المالك: «أكّد عليه قبل ما يقفل في الحاجات المهمّة
 * إنه هيقفل واللي هو كاتبه هيضيع»): كانت كل نافذةٍ ودرجٍ تسمع Esc على النافذة كلّها — فـEsc لإغلاق تقويمٍ أو صورة داخل
 * «حجز جديد» يقفل الحجز كلّه ويضيع ما كُتب. الآن مكدّسٌ واحد: آخر ما فُتح يستجيب وحده، وما يُغلق نفسه بـEsc قبله (تقويم،
 * قائمة) يعلّم الحدث `preventDefault` فلا يصل النافذة.
 */
const escLayers: symbol[] = [];
const escHandlers = new Map<symbol, () => void>();
/**
 * **زرّ «رجوع» في الموبايل يقفل الطبقة العليا أوّلًا** (المراجعة الشاملة الثانية 06/10/2026، المالك: «اشتغل عليهم»): كان «رجوع» في
 * تطبيق أندرويد يرجع في تاريخ الصفحة دائمًا — فنافذةٌ مفتوحة (حجز جديد، دفعة) تختفي مع الصفحة ويضيع ما كُتب فيها بلا سؤال.
 * الآن يُسأل هذا أوّلًا: فيه نافذة أو درج مفتوح؟ يُقفل هو وحده بطريق Esc نفسه (والنافذة التي فيها كلامٌ مكتوب تسأل قبل الإغلاق).
 */
export function closeTopLayer(): boolean {
  const id = escLayers[escLayers.length - 1];
  const fn = id ? escHandlers.get(id) : undefined;
  if (!fn) return false;
  fn();
  return true;
}
function useEscLayer(onEsc: () => void): void {
  const fn = useRef(onEsc);
  fn.current = onEsc;
  useEffect(() => {
    const id = Symbol('layer');
    escLayers.push(id);
    escHandlers.set(id, () => fn.current());
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || escLayers[escLayers.length - 1] !== id) return;
      e.preventDefault();
      fn.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = escLayers.indexOf(id);
      if (i >= 0) escLayers.splice(i, 1);
      escHandlers.delete(id);
    };
  }, []);
}

/**
 * قفل تمرير الصفحة **بالعدّ** (المراجعة الشاملة 02/10/2026): نافذتان تُغلقان معًا كانتا تعيدان الحالة بترتيبٍ معكوس فتبقى
 * الصفحة لا تتحرّك حتى إعادة التحميل. الآن أوّل قفلٍ يحفظ الحالة وآخر فكٍّ يعيدها.
 */
let scrollLocks = 0;
let savedOverflow = '';
let savedPadding = '';
function useBodyScrollLock(): void {
  useEffect(() => {
    const body = document.body;
    if (scrollLocks++ === 0) {
      savedOverflow = body.style.overflow; savedPadding = body.style.paddingRight;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      body.style.overflow = 'hidden';
      if (gap > 0) body.style.paddingRight = `${gap}px`;
    }
    return () => {
      if (--scrollLocks === 0) { body.style.overflow = savedOverflow; body.style.paddingRight = savedPadding; }
    };
  }, []);
}

export function Drawer({ title, onClose, children, side = 'right', tone = 'plain', width = 'md', footer }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** الحافة الفيزيائية: يسار الشاشة أو يمينها — لا تنعكس مع RTL. */
  side?: 'left' | 'right';
  /** `brand` = ترويسة كحلية بهوية iConTravel، `plain` = ترويسة بيضاء. */
  tone?: 'plain' | 'brand';
  width?: 'md' | 'lg';
  footer?: ReactNode;
}) {
  // قفل التمرير + تعويض الشريط — بالعدّ مع بقية الطبقات
  useBodyScrollLock();
  // Esc يغلق — وللطبقة العليا وحدها
  useEscLayer(onClose);

  const edge = side === 'left' ? 'left-0 border-r' : 'right-0 border-l';
  const brand = tone === 'brand';

  return createPortal(
    <div className="drawer-viewport fixed inset-x-0 top-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      {/* الخلفية: الضغط عليها يغلق، وهي معتمة قليلًا حتى يبقى سياق الصفحة مرئيًا */}
      <button type="button" aria-label="إغلاق" tabIndex={-1}
        className="absolute inset-0 h-full w-full cursor-default bg-ink-900/50" onClick={onClose} />
      <aside
        className={`drawer-viewport fixed top-0 ${edge} flex w-full ${width === 'lg' ? 'max-w-xl' : 'max-w-md'} flex-col border-slate-200 bg-slate-50 shadow-2xl`}>
        <header className={`flex shrink-0 items-center justify-between gap-3 px-4 py-3 ${
          brand ? 'bg-brand-700 text-white' : 'border-b border-slate-200 bg-white'}`}>
          <h2 className="text-lg font-bold">{title}</h2>
          <CloseX onClose={onClose} label="إغلاق الدرج" tone={brand ? 'brand' : 'plain'} />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4">{children}</div>
        {footer && <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3">{footer}</div>}
      </aside>
    </div>,
    document.body,
  );
}

/**
 * نافذة فوق الشاشة — **في وسطها** لا ملتصقة بأعلاها.
 *
 * كانت `items-start` تضع النافذة تحت حافة الصفحة مباشرةً، فتبدو كأنها جزء من الترويسة
 * وتُقتطع على الشاشات القصيرة. والآن: توسيط رأسي مع `my-auto` وسقف ارتفاع وتمرير داخلي —
 * فالنافذة القصيرة تتوسّط، والطويلة تُمرَّر داخل نفسها ولا يختفي رأسها فوق الحافة.
 *
 * و`size="sm"` لنوافذ السؤال القصير (تأكيد بصمة الحضور مثلًا): نافذة صغيرة في وسط الشاشة.
 *
 * **وتُبنى في `document.body` عبر بوّابة** (كالدرج): الترويسة عليها `backdrop-blur`، و
 * `backdrop-filter` يجعل من العنصر **كتلةً حاوية للعناصر الثابتة** داخله — فنافذة تُفتح من
 * زرّ في الترويسة كانت تُنسَب إلى صندوق الترويسة لا إلى الشاشة: تلتصق بأعلاها وتُقتطع عند
 * حافّتها مهما وُسِّطت. والبوّابة تُخرجها من ذلك كلّه فيصير `fixed inset-0` شاشةً بحقّ.
 */
/**
 * **زرّ ✕ للإغلاق** (قرار المالك 01/10/2026: «بدل إغلاق ده وأفضل أطلع لفوق، خلّي فيه علامة X تخلّيني أقفل وخلاص من
 * غير ما أطلع — وطبّق ده على أي حاجة»): علامةٌ واحدة في كل نافذة ودرجٍ وبطاقةٍ تُغلق، ورأسُ النافذة ثابتٌ فوق
 * فيبقى ✕ ظاهرًا مهما نزلت. والاسم للقارئ الآلي «إغلاق» كما كان.
 */
/*
 * **ثم: «اظهر علامة الـX دي علشان مش ظاهرة، وادّيها لون مختلف وتصميم مميّز»** (قرار المالك 02/10/2026): كانت دائرةً
 * بيضاء بحدٍّ رماديّ تذوب في رأس النافذة الأبيض. الآن دائرةٌ حمراء متدرّجة بعلامةٍ بيضاء عريضة (SVG حادّ لا حرفٌ
 * يختلف بالخط) وظلٍّ وحلقة، وتدور قليلًا وتكبر عند المرور — تُرى من أوّل نظرة. وعلى الرؤوس الملوّنة: دائرةٌ بيضاء بعلامة حمراء.
 */
export function CloseX({ onClose, label = 'إغلاق', tone = 'plain' }: { onClose: () => void; label?: string; tone?: 'plain' | 'brand' }) {
  return (
    <button type="button" onClick={onClose} aria-label={label} title={label}
      className={`group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full shadow-md ring-2 transition duration-200 hover:rotate-90 hover:scale-110 focus-visible:outline-none focus-visible:ring-4 active:scale-95 ${
        tone === 'brand'
          ? 'bg-white text-rose-600 ring-white/40 hover:bg-rose-50'
          : 'bg-gradient-to-br from-rose-500 to-rose-700 text-white shadow-rose-600/30 ring-rose-200 hover:from-rose-600 hover:to-rose-800'}`}>
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  );
}

export function Modal({ title, onClose, size = 'lg', children, confirmClose = false, holdClose = false }: {
  title: string; onClose: () => void; size?: 'sm' | 'lg' | 'xl' | 'full'; children: ReactNode;
  /** فيها كلامٌ مكتوب لم يُحفظ ⇒ الإغلاق (✕ أو Esc) يسأل أولًا (قرار المالك 02/10/2026) */
  confirmClose?: boolean;
  /** حفظٌ جارٍ ⇒ لا إغلاق حتى ينتهي — وإلّا ضاع ما بعد الحفظ (المرفقات) بلا علم أحد */
  holdClose?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const tryClose = () => {
    if (holdClose) return;
    if (confirmClose && !asking) { setAsking(true); return; }
    onClose();
  };
  // Escape يغلق النافذة كما يغلق الدرج — للطبقة العليا وحدها
  useEscLayer(tryClose);
  // والصفحة خلفها ثابتة ما دامت النافذة مفتوحة (02/10/2026) — بالعدّ مع بقية الطبقات
  useBodyScrollLock();
  return createPortal(
    // **شريط تمرير واحد — شريط النافذة نفسها** (قرار المالك 02/10/2026: «في سكرول على الشمال خالص غلط وملوش أي لازمة،
    // وبينزّلني في فراغ — الغيه أو ظبّطه»): الخلفية المعتمة كانت تُمرَّر هي أيضًا (overflow-y-auto)، فيظهر لها شريطٌ على
    // يسار الشاشة، وتمريرُه يرفع النافذة ويترك فراغًا. النافذة محدودة الارتفاع وتُمرَّر من داخلها، فالخلفية لا تتحرّك.
    // و`dvh` لا `vh`: ارتفاع الشاشة الظاهر فعلًا — فلا تزيد النافذة عنه بشريط المتصفّح أو بالتكبير.
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden overscroll-contain bg-ink-900/40 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`card max-h-[calc(100dvh-1.5rem)] w-full overflow-y-auto overscroll-contain p-4 sm:max-h-[calc(100dvh-3rem)] sm:p-6 ${
        size === 'sm' ? 'max-w-md' : size === 'xl' ? 'max-w-5xl' : size === 'full' ? 'max-w-[110rem]' : 'max-w-3xl'}`}>
        {/* رأسٌ ثابتٌ فوق داخل النافذة: ✕ ظاهرة مهما نزل المحتوى (01/10/2026) */}
        <header className="sticky -top-4 z-10 -mx-4 -mt-4 mb-4 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:-top-6 sm:-mx-6 sm:-mt-6 sm:px-6">
          <h2 className="text-lg font-semibold">{title}</h2>
          <CloseX onClose={tryClose} />
        </header>
        {asking && (
          <div role="alertdialog" aria-label="تأكيد الإغلاق"
            className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span className="flex-1 font-semibold">هتقفل والبيانات اللي كتبتها هتضيع — متأكد؟</span>
            <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700" onClick={onClose}>اقفل من غير حفظ</button>
            <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setAsking(false)}>رجوع للنموذج</button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
